/**
 * Pure-function "needs attention" tab derivation for the Advanced
 * Settings sub-tab warning indicators (Multi-Add Phase 2).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). Sibling to
 * `chip-state.ts` — both read the same `StatusDevice` protobuf
 * snapshot, just with different surface contracts:
 *
 *   - `chip-state.ts` returns a single priority-cascaded chip
 *     (one warning per tile).
 *   - `deriveNeedAttentionTabs` returns FOUR independent booleans
 *     (one per Advanced Settings sub-tab), because the user is
 *     looking at the tabs side-by-side and needs to see which ones
 *     have unresolved issues without picking a single "winner".
 *
 * See knowledge: `multi-writer-registry-completeness`
 * (ai.knowledge/platform/studio.client) — analogous shape: the helper
 * always returns ALL FOUR keys (`authentication` / `video` / `control`
 * / `driver`), never a partial object. Downstream consumers can index
 * `tabs[tab.value]` without a `?? false` guard.
 *
 * Per `think-in-systems` (ai.knowledge/global): one pure helper per
 * semantics. The chip cascade and the per-tab booleans are different
 * concerns even though they read the same protobuf surface — keep
 * them as parallel helpers rather than overloading one return shape.
 *
 * Per `stay-in-scope` (ai.knowledge/global): same input contract as
 * `deriveChipState` — `Pick<Device, 'agentStatus' | 'modelId'>` shape
 * — so existing test fixtures and call-site adapters can be reused.
 *
 * Connection warnings require live status and respect session dismissals.
 * The generic-driver warning is configuration-based: it remains visible until
 * a specific model, or "Other – brand is not listed", is selected, including
 * while the bridge is offline.
 */
import {
  AuthenticationStatus,
  StatusDeviceConnectionStatus
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StatusDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { Agent } from '@studio/models/agent';
import { needsDriverChoice } from '#~/device/setup-attention.js';
import type { Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';

/**
 * The four Advanced Settings sub-tabs that can flag "needs attention".
 *
 * The fifth Advanced Settings tab — `monitoring` — is intentionally
 * NOT in this set: it surfaces bridge / agent state, not per-aspect
 * device-config issues, so the warning icon never lights up there.
 *
 * Per `think-in-systems` (ai.knowledge/global): one type alias for
 * the tab keys; downstream code uses
 * `keyof NeedAttentionTabs` to enumerate.
 */
export type NeedAttentionTabs = Readonly<{
  authentication: boolean;
  video: boolean;
  control: boolean;
  driver: boolean;
}>;

/**
 * Per-tab dismissal map. Each key is `true` when the user has
 * dismissed the warning for that tab in the current session;
 * undefined / missing means "not dismissed".
 *
 * Same shape as `State.dismissedNeedAttentionTabs[deviceId]` on the
 * depot — pass that slice in directly (default to `{}` when the
 * device has no dismissal record yet).
 */
export type NeedAttentionDismissed = Partial<Record<keyof NeedAttentionTabs, true>>;

/**
 * Derive the per-sub-tab "needs attention" booleans for a single device.
 *
 * @param status - Raw `StatusDevice` protobuf snapshot from the agent
 *   state-sync tree (typically `proxy?.state.status?.device[deviceId]`).
 *   `undefined` means the bridge has not yet sent a status for this
 *   device — connection warnings remain hidden until status arrives.
 * @param device - Subset of the studio Device. Same `Pick<Device,
 *   'agentStatus' | 'modelId'>` shape as `deriveChipState` so test
 *   fixtures can be shared. `agentStatus` gates the offline branches;
 *   `modelId` and `model` drive the `driver` boolean (`needsDriverChoice`).
 * @param dismissed - Per-tab dismissal map from the depot. Pass `{}`
 *   when the device has no dismissal record. Driver warnings persist
 *   while the generic model is selected.
 * @returns `NeedAttentionTabs` — ALL FOUR keys ALWAYS present (no
 *   `Partial`, no missing keys). Downstream tab renderer indexes
 *   `tabs[tab.value]` without a `?? false` guard.
 */
export const deriveNeedAttentionTabs = (
  status: T_Maybe<StatusDevice>,
  device: {
    agentStatus?: T_Maybe<Agent['status']>;
    modelId?: T_Maybe<Model['id']>;
  },
  dismissed: NeedAttentionDismissed,
  model?: Parameters<typeof needsDriverChoice>[1]
): NeedAttentionTabs => {
  // Driver choice is known locally and does not depend on bridge connectivity.
  const driverMissing = needsDriverChoice(device.modelId, model);
  if (
    status === undefined ||
    device.agentStatus === 'offline' ||
    device.agentStatus === 'offline-embedded'
  ) {
    return {
      authentication: false,
      video: false,
      control: false,
      driver: driverMissing
    };
  }

  // Auth INVALID on EITHER aspect surfaces as the `authentication` tab
  // warning. Per Extended Discovery invariant #9 (per-aspect auth
  // verdict), video and movement each carry their own
  // `authenticationStatus`; either INVALID is enough to flag the tab.
  const authInvalid =
    status.video?.authenticationStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID ||
    status.movement?.authenticationStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID;

  // Video transport FAILED — terminal state only (CONNECTING / UNKNOWN
  // intentionally fall through to false). Same terminal-only contract
  // as the chip cascade.
  const videoFailed =
    status.video?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED;

  // Movement (control) transport FAILED — same terminal-only rule.
  const controlFailed =
    status.movement?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED;

  // Connection dismissals never hide the generic driver's limited capabilities.
  return {
    authentication: authInvalid && dismissed.authentication !== true,
    video: videoFailed && dismissed.video !== true,
    control: controlFailed && dismissed.control !== true,
    driver: driverMissing
  };
};
